import React from 'react';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  variant?: 'default' | 'hover' | 'interactive';
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export function Card({ 
  children, 
  className = '', 
  variant = 'default',
  size = 'md',
  ...props 
}: CardProps) {
  const variants = {
    default: 'card',
    hover: 'card-hover',
    interactive: 'card-interactive'
  };

  const sizes = {
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
    xl: 'p-10'
  };

  return (
    <div 
      className={`${variants[variant]} ${size !== 'md' ? sizes[size] : ''} animate-in ${className}`} 
      {...props}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
}

export function CardHeader({ children, className = '', ...props }: CardHeaderProps) {
  return (
    <div className={`section-header ${className}`} {...props}>
      {children}
    </div>
  );
}

interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function CardTitle({ 
  children, 
  className = '', 
  size = 'md',
  ...props 
}: CardTitleProps) {
  const sizes = {
    sm: 'heading-sm',
    md: 'section-title',
    lg: 'heading-lg'
  };

  return (
    <h3 className={`${sizes[size]} ${className}`} {...props}>
      {children}
    </h3>
  );
}

interface CardSubtitleProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode;
  className?: string;
}

export function CardSubtitle({ children, className = '', ...props }: CardSubtitleProps) {
  return (
    <p className={`section-subtitle ${className}`} {...props}>
      {children}
    </p>
  );
}

interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  scrollable?: boolean;
}

export function CardContent({ 
  children, 
  className = '', 
  scrollable = false,
  ...props 
}: CardContentProps) {
  const scrollClass = scrollable ? 'overflow-y-auto scrollbar-custom max-h-96' : '';
  
  return (
    <div className={`${scrollClass} ${className}`} {...props}>
      {children}
    </div>
  );
}

interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
}

export function CardFooter({ children, className = '', ...props }: CardFooterProps) {
  return (
    <div className={`mt-6 pt-4 border-t border-neutral-100 ${className}`} {...props}>
      {children}
    </div>
  );
}